/**
 * TanStack Query hooks for CD Documents
 */

import {
  type UseMutationOptions,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { closingDisclosureApi } from '../services/closingDisclosureApi';
import type { CDDocument, DocumentType } from '../types';
import { cdQueryKeys } from './useClosingDisclosureQueries';
import { versionQueryKeys } from './useVersions';

/**
 * Extended query keys for documents
 */
export const documentQueryKeys = {
  ...cdQueryKeys,
  documents: (transactionId: string) =>
    [...cdQueryKeys.transaction(transactionId), 'documents'] as const,
  document: (transactionId: string, documentId: string) =>
    [...documentQueryKeys.documents(transactionId), documentId] as const,
  sourceDocuments: (transactionId: string) =>
    [...documentQueryKeys.documents(transactionId), 'source'] as const,
};

// ============================================================================
// Document Queries
// ============================================================================

/**
 * Fetch all documents for a transaction
 */
export function useDocuments(
  transactionId: string,
  _documentType?: DocumentType,
  options?: Omit<UseQueryOptions<CDDocument[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: [...documentQueryKeys.documents(transactionId), _documentType],
    queryFn: async () => {
      const response = await closingDisclosureApi.getDocuments(transactionId);
      return response.data.documents as unknown as CDDocument[];
    },
    ...options,
    enabled: (options?.enabled ?? !!transactionId) && !!transactionId,
  });
}

/**
 * Fetch source documents for a transaction
 */
export function useSourceDocuments(
  transactionId: string,
  options?: Omit<UseQueryOptions<CDDocument[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: documentQueryKeys.sourceDocuments(transactionId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getSourceDocuments(transactionId);
      return response.data.documents as unknown as CDDocument[];
    },
    enabled: !!transactionId,
    ...options,
  });
}

/**
 * Fetch a specific document
 */
export function useDocument(
  transactionId: string,
  documentId: string,
  options?: Omit<UseQueryOptions<CDDocument, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: documentQueryKeys.document(transactionId, documentId),
    queryFn: async () => {
      const response = await closingDisclosureApi.getDocument(transactionId, documentId);
      return response.data as unknown as CDDocument;
    },
    enabled: !!transactionId && !!documentId,
    ...options,
  });
}

// ============================================================================
// Document Mutations
// ============================================================================

/**
 * Upload a document
 */
export function useUploadDocument(
  options?: Omit<
    UseMutationOptions<
      { document: CDDocument; message: string },
      Error,
      {
        transactionId: string;
        file: File;
        documentType?: DocumentType;
        createVersion?: boolean;
      }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, file, documentType = 'cd', createVersion = true }) => {
      const response = await closingDisclosureApi.uploadDocument(transactionId, file, documentType);
      void createVersion; // Used for cache invalidation
      return { document: response.data as unknown as CDDocument, message: 'Document uploaded' };
    },
    onSuccess: (_, variables) => {
      // Invalidate documents list
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.documents(variables.transactionId),
      });
      // If a source_cd was uploaded with createVersion, also invalidate versions
      if (variables.documentType === 'source_cd' && variables.createVersion !== false) {
        queryClient.invalidateQueries({
          queryKey: versionQueryKeys.versions(variables.transactionId),
        });
        queryClient.invalidateQueries({
          queryKey: versionQueryKeys.latestVersion(variables.transactionId),
        });
      }
    },
    ...options,
  });
}

/**
 * Download a document
 */
export function useDownloadDocument(
  options?: Omit<
    UseMutationOptions<
      { blob: Blob; filename: string },
      Error,
      { transactionId: string; documentId: string }
    >,
    'mutationFn'
  >,
) {
  return useMutation({
    mutationFn: async ({ transactionId, documentId }) => {
      const blob = await closingDisclosureApi.downloadDocument(transactionId, documentId);
      return { blob, filename: `document-${documentId}.pdf` };
    },
    onSuccess: (data) => {
      // Trigger download in browser
      const url = window.URL.createObjectURL(data.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    },
    ...options,
  });
}

/**
 * Delete a document
 */
export function useDeleteDocument(
  options?: Omit<
    UseMutationOptions<void, Error, { transactionId: string; documentId: string }>,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, documentId }) => {
      await closingDisclosureApi.deleteDocument(transactionId, documentId);
    },
    onSuccess: (_, variables) => {
      // Invalidate documents list
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.documents(variables.transactionId),
      });
    },
    ...options,
  });
}

/**
 * Process document (works for pending and failed documents)
 */
export function useProcessDocument(
  options?: Omit<
    UseMutationOptions<
      {
        success: boolean;
        data: {
          documentId: string;
          processingStatus: string;
          validationEnabled?: boolean;
        };
      },
      Error,
      { transactionId: string; documentId: string }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId }) => {
      return closingDisclosureApi.processDocument(documentId);
    },
    onSuccess: (_, variables) => {
      // Invalidate documents list
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.documents(variables.transactionId),
      });
    },
    ...options,
  });
}

/**
 * Retry document processing (deprecated - use useProcessDocument instead)
 */
export function useRetryDocument(
  options?: Omit<
    UseMutationOptions<
      { success: boolean; data: { documentId: string; processingStatus: string } },
      Error,
      { transactionId: string; documentId: string }
    >,
    'mutationFn'
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ documentId }) => {
      return closingDisclosureApi.processDocument(documentId);
    },
    onSuccess: (_, variables) => {
      // Invalidate documents list
      queryClient.invalidateQueries({
        queryKey: documentQueryKeys.documents(variables.transactionId),
      });
    },
    ...options,
  });
}
